import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plug, CheckCircle2, XCircle, ShieldAlert } from 'lucide-react';
import { Panel, Badge, Disclaimer } from '../../components/Primitives';
import { api, errMsg, fmtDate } from '../../lib/api';

const inp = 'w-full h-11 px-3 rounded-lg bg-vj-charcoal border border-border text-sm text-foreground focus:border-vj-neon/60 focus:outline-none';

export default function AdminExchange() {
  const [cfg, setCfg] = useState(null);
  const [address, setAddress] = useState('');
  const [key, setKey] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/admin/exchange').then(({ data }) => {
    setCfg(data);
    setAddress(data.account_address || '');
    setEnabled(data.enabled || false);
  }).catch((err) => toast.error(`Could not load exchange config: ${errMsg(err)}`));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!/^0x[0-9a-fA-F]{40}$/.test(address.trim())) return toast.error('Enter a valid 0x account address');
    setBusy(true);
    try {
      const payload = { account_address: address.trim(), enabled };
      if (key.trim()) payload.api_wallet_private_key = key.trim();
      await api.post('/admin/exchange', payload);
      setKey('');
      toast.success('Exchange configuration saved');
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await api.post('/admin/exchange/test');
      setTestResult(data);
      toast.success('Hyperliquid connection OK');
      load();
    } catch (err) { setTestResult({ ok: false }); toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-exchange">
      <Panel title="Hyperliquid Exchange Integration" action={
        cfg?.configured ? <Badge tone={cfg.enabled && cfg.api_wallet_set ? 'green' : 'gold'}>{cfg.enabled && cfg.api_wallet_set ? 'Live trading ready' : 'Configured'}</Badge> : <Badge tone="grey">Not configured</Badge>
      }>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-vj-neon/10 grid place-items-center"><Plug size={20} className="text-vj-neon" /></div>
          <div>
            <div className="text-sm font-medium">Hyperliquid · mainnet</div>
            <div className="text-xs text-vj-grey">Paste the account wallet address and (optionally) an API wallet key authorized to sign orders.</div>
          </div>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs text-vj-grey">Account wallet address</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x… main account address" className={`${inp} font-mono mt-1`} autoComplete="off" disabled={!cfg} data-testid="exchange-address" />
          </div>
          <div>
            <label className="text-xs text-vj-grey">API wallet private key (optional — enables order signing)</label>
            <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg?.api_wallet_set ? '•••••••• (saved — leave blank to keep)' : '0x… API wallet private key'} className={`${inp} font-mono mt-1`} autoComplete="off" disabled={!cfg} data-testid="exchange-key" />
            <p className="text-[11px] text-vj-grey mt-1">Stored encrypted server-side (never returned to the browser). This is an EVM API-wallet key, not a withdrawal key.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-vj-neon" disabled={!cfg} data-testid="exchange-enabled" />
            Enable live trading (requires a saved API wallet key)
          </label>
          <div className="flex gap-2">
            <button disabled={busy || !cfg} className="h-11 px-6 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm vj-glow-green" data-testid="exchange-save">Save</button>
            <button type="button" onClick={test} disabled={busy || !cfg?.configured} className="h-11 px-6 rounded-lg border border-border text-sm hover:border-vj-neon/50" data-testid="exchange-test">Test read-only connection</button>
          </div>
        </form>

        {testResult && (
          <div className="mt-5 rounded-lg border border-border p-4" data-testid="exchange-test-result">
            {testResult.ok ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-vj-neon"><CheckCircle2 size={16} /> Live account state retrieved</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 font-mono text-xs">
                  <div><div className="text-vj-grey">Account value</div>${testResult.account_value ?? '—'}</div>
                  <div><div className="text-vj-grey">Withdrawable</div>${testResult.withdrawable ?? '—'}</div>
                  <div><div className="text-vj-grey">Margin used</div>${testResult.total_margin_used ?? '—'}</div>
                  <div><div className="text-vj-grey">Open positions</div>{testResult.open_positions ?? 0}</div>
                </div>
              </div>
            ) : <div className="flex items-center gap-2 text-vj-red text-sm"><XCircle size={16} /> Connection test failed</div>}
          </div>
        )}
        {cfg?.last_test && <div className="text-[11px] text-vj-grey mt-3">Last test: {cfg.last_test} · {fmtDate(cfg.last_test_at)}</div>}
      </Panel>

      <Disclaimer>
        Read-only account state uses Hyperliquid's public info API (live). Order execution requires a saved API wallet key and the "Enable live trading" switch. Automated bot execution remains disabled by default and is gated behind this configuration and per-market risk checks. On-chain withdrawal signing is intentionally not enabled.
      </Disclaimer>
    </div>
  );
}
